/**
 * A sub-agent tool call (research / review_ui / review_code / test_backend)
 * with its live activity nested underneath. While running the lane is
 * live-expanded and windowed to the latest steps; on completion it collapses
 * to one line with the summary available on expand. The workbench SubAgentPanel
 * (via the open-in-panel button) remains the full-log view.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ToolPart } from '~/components/chat/message-parts';
import { plainStep } from './plain-steps';
import { StepTooltip } from './ToolLine';
import WithTooltip from '~/components/ui/Tooltip';
import { ToolDetail } from './ToolDetail';
import { DuneaSpinner } from './DuneaSpinner';
import { LIVE_WINDOW } from './ToolGroup';
import { openSubAgentPanel } from '~/lib/stores/sub-agent';
import { workbenchStore } from '~/lib/stores/workbench';

export type AgentLog = { label: string; steps: string[]; streamedText: string; status: string };

export function SubAgentGroup({
  part,
  agentLog,
  isStreaming = false,
}: {
  part: ToolPart;
  agentLog?: AgentLog;
  isStreaming?: boolean;
}) {
  const isComplete = part.state === 'output-available';
  const isError = part.state === 'output-error';
  const running = !isComplete && !isError && isStreaming;
  const [expanded, setExpanded] = useState(running);
  const wasRunning = useRef(running);

  useEffect(() => {
    if (wasRunning.current && !running) {
      setExpanded(false);
    }

    if (!wasRunning.current && running) {
      setExpanded(true);
    }

    wasRunning.current = running;
  }, [running]);

  const steps = agentLog?.steps ?? [];
  const visibleSteps = running && expanded ? steps.slice(-LIVE_WINDOW) : steps;
  const hidden = steps.length - visibleSteps.length;
  const step = plainStep(part, !running);

  const handleOpenPanel = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      const tcId = ((part.output as any)?.toolCallId as string | undefined) ?? part.toolCallId;

      if (tcId) {
        openSubAgentPanel(tcId);
        workbenchStore.openTool('sub-agent');
      }
    },
    [part.output, part.toolCallId],
  );

  return (
    <div className="my-1.5">
      <WithTooltip
        tooltip={<StepTooltip explain={step.explain} tech={agentLog?.label} />}
        position="top"
        delay={300}
        maxWidth={320}
      >
        <div
          className="group/sa flex items-center gap-2 py-[3px] px-1.5 -mx-1.5 rounded-md cursor-pointer select-none transition-colors hover:bg-white/[0.025]"
          onClick={() => setExpanded(!expanded)}
        >
          {running ? (
            <DuneaSpinner size={13} className="text-accent flex-shrink-0" />
          ) : (
            <span className={`${step.icon} text-[13px] flex-shrink-0 text-faint`} />
          )}
          <span className={`text-[13.5px] truncate min-w-0 ${running ? 'text-secondary ui-pulsing' : 'text-muted'}`}>
            {step.text}
          </span>
          {(isComplete || isError) && (
            <WithTooltip tooltip="See the full review" position="top" delay={300}>
              <button
                onClick={handleOpenPanel}
                aria-label="See the full review"
                className="text-[11px] text-faint hover:text-accent bg-transparent border-0 cursor-pointer flex-shrink-0 flex items-center gap-1"
                type="button"
              >
                <span className="i-ph:arrow-square-out text-[12px]" />
              </button>
            </WithTooltip>
          )}
        </div>
      </WithTooltip>
      {expanded && (
        <div className="ml-[6px] pl-3.5 border-l border-hairline">
          {hidden > 0 && <div className="text-[11px] text-faint py-0.5">… {hidden} earlier</div>}
          {visibleSteps.map((line, i) => (
            <div key={i} className="py-0.5 text-[12px] text-faint truncate">
              {line}
            </div>
          ))}
          {(isComplete || isError) && (
            <div className="my-1.5 rounded-lg border border-hairline px-3.5 py-3" onClick={(e) => e.stopPropagation()}>
              <ToolDetail part={part} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
